import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  PropsWithChildren,
  Dispatch,
  SetStateAction,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import commentsAPI, { NoteComment } from '../api/comments';
import {
  FilterType,
  getPersonalNotesAPI,
  PersonalNotesFilterRequest,
} from '../api/personal-notes';
import { Role } from '../api/user/Role';
import { Attachment } from '../components/common/attachment-card';
import { ProtectedRouteProps } from '../router/type';
import { decodeQuery, encodeQuery, isMobile } from '../utils/functions';
import { useResourceBundles } from './resource-bundles-context';
import { UserContext } from './user-context';

type BeforeUnloadState = {
  action: 'add' | 'select' | 'close';
  note?: PersonalNote;
};

export enum NoteType {
  GeneralNote = 'GeneralNote',
  VentureNote = 'VentureNote',
  EventNote = 'EventNote',
  FounderNote = 'FounderNote',
  MentorNote = 'MentorNote',
}

export enum NotesBlockType {
  Attachment = 'Attachment',
  Comment = 'Comment',
}

interface CommonPersonalNote {
  id?: string;
  tenantId: string;
  type: keyof typeof NoteType;
  date: string;
  content: string;
  attachmentRefs?: string;
  parentId?: string;
  valid?: boolean;
  ventureName?: string | null;
  authorName?: string | null;
  referredName?: string | null;
  isTargetNote?: boolean;
}

export interface MentorPersonalNote extends CommonPersonalNote {
  mentorId: string | null;
}

export interface FounderPersonalNote extends CommonPersonalNote {
  founderId: string | null;
}

export type PersonalNote = MentorPersonalNote | FounderPersonalNote;

export interface PersonalNotesContextProps {
  notes: PersonalNote[];
  selectedNote: PersonalNote | null;
  isReadOnlySelectedNote: boolean;
  attachments: Attachment[];
  showAttachments: boolean;
  comments: NoteComment[];
  activeBlock: NotesBlockType | null;
  loadingComments: boolean;
  showComments: boolean;
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  searchFilter: string | undefined;
  typeFilter: FilterType | undefined;
  searchValue: string;
  value: string;
  isDirty: boolean;
  beforeUnloadState: BeforeUnloadState | null;
  typeFilterOptions: { label: string; value: FilterType | undefined }[];
  handleSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setBeforeUnloadState: Dispatch<SetStateAction<BeforeUnloadState | null>>;
  setValue: Dispatch<SetStateAction<string>>;
  setSearchValue: Dispatch<SetStateAction<string>>;
  setSelectedNote: Dispatch<SetStateAction<PersonalNote | null>>;
  setAttachments: Dispatch<SetStateAction<Attachment[]>>;
  toggleAttachments: () => void;
  setComments: Dispatch<SetStateAction<NoteComment[]>>;
  setLoadingComments: Dispatch<SetStateAction<boolean>>;
  toggleComments: () => void;
  setSaving: (saving: boolean) => void;
  closeAllBlocks: () => void;
  createNote: (note: PersonalNote) => Promise<PersonalNote | void>;
  updateNote: (note: PersonalNote) => Promise<PersonalNote | void>;
  deleteNote: (note: PersonalNote) => Promise<PersonalNote | void>;
  onNextPage: () => void;
  setSearchFilter: (searchFilter: string | undefined) => void;
  setTypeFilter: (typeFilter: FilterType | undefined) => void;
  deleteComment: (commentId: string) => Promise<void>;
  resetFilters: () => void;
  handleSaveNote: () => Promise<void>;
  handleNoteRemove: (callback: Function) => Promise<void>;
  addNewNote: () => void;
  handleAddNewNote: () => void;
  handleCardSelect: (note: PersonalNote) => void;
  handleCardClose: () => void;
}

export const PersonalNotesContext = createContext<PersonalNotesContextProps>({
  notes: [],
  selectedNote: null,
  isReadOnlySelectedNote: false,
  attachments: [],
  showAttachments: false,
  comments: [],
  activeBlock: null,
  loadingComments: false,
  showComments: false,
  loading: false,
  saving: false,
  deleting: false,
  searchFilter: undefined,
  typeFilter: undefined,
  searchValue: '',
  value: '',
  isDirty: false,
  beforeUnloadState: null,
  typeFilterOptions: [],
  handleSearch: () => {},
  setBeforeUnloadState: () => {},
  setValue: () => {},
  setSearchValue: () => {},
  setSelectedNote: () => {},
  setAttachments: () => {},
  toggleAttachments: () => {},
  setComments: () => {},
  setLoadingComments: () => {},
  toggleComments: () => {},
  setSaving: () => {},
  closeAllBlocks: () => {},
  createNote: async () => {},
  updateNote: async () => {},
  deleteNote: async () => {},
  onNextPage: () => {},
  setSearchFilter: () => {},
  setTypeFilter: () => {},
  deleteComment: async () => {},
  resetFilters: () => {},
  handleSaveNote: async () => {},
  handleNoteRemove: async () => {},
  addNewNote: () => {},
  handleAddNewNote: () => {},
  handleCardSelect: () => {},
  handleCardClose: () => {},
});

const getEncodedSearchValue = (search: string): string => {
  // remove http(s):// part from the string
  const filteredSearch = search
    .replace(/https?:\/\/[^ ]+/g, '')
    .replace(/[^a-zA-Z0-9\s\-_.!~*'(),@?]/g, '');
  const encodedValue = encodeURIComponent(filteredSearch);
  return encodedValue;
};

const defaultFilters: PersonalNotesFilterRequest = {
  search: undefined,
  filterType: undefined,
  pageNumber: 0,
};

// Read query params synchronously before first render
const getInitialFiltersFromURL = (): PersonalNotesFilterRequest => {
  if (typeof window === 'undefined') return defaultFilters;

  const params = new URLSearchParams(window.location.search);
  const filterParam = params.get('filter');

  if (!filterParam) return defaultFilters;

  try {
    const decodedFilter = decodeQuery(filterParam) as {
      search?: string;
      filterType?: FilterType;
      pageNumber?: number;
    };

    return {
      search: decodedFilter.search,
      filterType: decodedFilter.filterType,
      pageNumber: decodedFilter.pageNumber ?? 0,
    };
  } catch (e) {
    return defaultFilters;
  }
};

// Read note and targetNote from URL
const getInitialNoteFromURL = (): {
  note: string | null;
  targetNote: string | null;
} => {
  if (typeof window === 'undefined') return { note: null, targetNote: null };
  const params = new URLSearchParams(window.location.search);
  return {
    note: params.get('note'),
    targetNote: params.get('targetNote'),
  };
};

export const PersonalNotesProvider = ({
  children,
  user,
}: PropsWithChildren<ProtectedRouteProps>) => {
  const location = useLocation();
  const { rb } = useResourceBundles();
  const { tokenData, hasRole } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [notes, setNotes] = useState<PersonalNote[]>([]);
  const [selectedNote, setSelectedNote] = useState<PersonalNote | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [activeBlock, setActiveBlock] = useState<NotesBlockType | null>(null);
  const [comments, setComments] = useState<NoteComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const { initialFilters, initialNotes, initialSearchValue } = useMemo(() => {
    const initialFilters = getInitialFiltersFromURL();
    const initialNotes = getInitialNoteFromURL();

    // Decode search for display
    const initialSearchValue = initialFilters.search
      ? decodeURIComponent(initialFilters.search)
      : '';
    return { initialFilters, initialNotes, initialSearchValue };
  }, []);

  const [searchValue, setSearchValue] = useState(initialSearchValue);
  const [filters, setFilters] =
    useState<PersonalNotesFilterRequest>(initialFilters);
  const [hasMorePages, setHasMorePages] = useState<boolean>(true);
  const [value, setValue] = useState('');
  const [beforeUnloadState, setBeforeUnloadState] =
    useState<BeforeUnloadState | null>(null);

  // Computed values from filters
  const searchFilter = filters.search;
  const typeFilter = filters.filterType;

  // Query param targets parsed on first mount
  const targetNoteRef = useRef<string | null>(initialNotes.targetNote);
  const noteRef = useRef<string | null>(initialNotes.note);
  const createdNoteRef = useRef<PersonalNote | null>(null);
  const pendingNewNoteRef = useRef<PersonalNote | null>(null);
  const debouncedTimer = useRef<number>();
  const isInitialLoadRef = useRef<boolean>(true);
  const loadingRef = useRef<boolean>(false);

  const typeFilterOptions = useMemo(() => {
    const allOptions = [
      { label: 'All Types', value: undefined as FilterType | undefined },
      { label: 'Personal Notes', value: FilterType.OWN_NOTES },
      { label: 'Session Notes', value: FilterType.EVENT_NOTES },
      { label: 'Venture Notes', value: FilterType.VENTURE_NOTES },
      { label: `${rb('mentor-u')} Notes`, value: FilterType.MENTOR_NOTES },
      { label: 'Founder Notes', value: FilterType.FOUNDER_NOTES },
    ];

    const getAllowedFilterTypes = (): Set<FilterType | undefined> => {
      if (hasRole(Role.Founder)) {
        return new Set([
          undefined,
          FilterType.OWN_NOTES,
          FilterType.EVENT_NOTES,
        ]);
      }
      if (hasRole(Role.Mentor)) {
        return new Set([
          undefined,
          FilterType.OWN_NOTES,
          FilterType.EVENT_NOTES,
          FilterType.VENTURE_NOTES,
        ]);
      }
      return new Set([
        undefined,
        FilterType.OWN_NOTES,
        FilterType.EVENT_NOTES,
        FilterType.VENTURE_NOTES,
        FilterType.MENTOR_NOTES,
        FilterType.FOUNDER_NOTES,
      ]);
    };

    const allowedTypes = getAllowedFilterTypes();
    return allOptions.filter((option) => allowedTypes.has(option.value));
  }, [rb, hasRole]);

  const isDirty = useMemo(() => {
    if (!selectedNote) {
      return false;
    }
    return selectedNote?.content !== value;
  }, [selectedNote, value]);

  const currentPersonalNotesAPI = useMemo(() => {
    if (hasRole(Role.Admin) || hasRole(Role.Manager)) {
      return getPersonalNotesAPI('admin');
    } else if (hasRole(Role.Founder)) {
      return getPersonalNotesAPI('founder');
    } else {
      return getPersonalNotesAPI('mentor');
    }
  }, [hasRole]);

  // Check if Remove button should be hidden
  const isReadOnlySelectedNote = useMemo(() => {
    if (!selectedNote) return false;

    const isFounderOrMentor = hasRole(Role.Founder) || hasRole(Role.Mentor);
    const isGeneralNote = selectedNote.type === NoteType.GeneralNote;
    const ownerId =
      ('founderId' in selectedNote && selectedNote.founderId) ||
      ('mentorId' in selectedNote && selectedNote.mentorId) ||
      null;
    const userId = tokenData?.identityid;

    return isFounderOrMentor && isGeneralNote && userId !== ownerId;
  }, [selectedNote, hasRole, tokenData?.identityid]);

  const setLoadingState = useCallback((loading: boolean) => {
    loadingRef.current = loading;
    setLoading(loading);
  }, []);

  const setSearchFilter = useCallback(
    (search: SetStateAction<string | undefined>) => {
      setFilters((prev) => ({
        ...prev,
        search:
          typeof search === 'function'
            ? search(prev.search ?? undefined)
            : search ?? undefined,
        pageNumber: 0,
      }));
    },
    [],
  );

  const setTypeFilter = useCallback(
    (typeFilter: SetStateAction<FilterType | undefined>) => {
      setFilters((prev) => ({
        ...prev,
        filterType:
          typeof typeFilter === 'function'
            ? typeFilter(prev.filterType ?? undefined)
            : typeFilter ?? undefined,
        pageNumber: 0,
      }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setSearchValue('');
    setFilters(defaultFilters);
  }, []);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      clearTimeout(debouncedTimer.current);
      setSearchValue(e.target.value);

      debouncedTimer.current = setTimeout(() => {
        if (e.target.value.trim().length > 2) {
          const encodedValue = getEncodedSearchValue(e.target.value);
          setSearchFilter(encodedValue);
        } else {
          setSearchFilter(undefined);
        }
      }, 800) as unknown as number;
    },
    [setSearchFilter],
  );

  const fetchTargetNote = useCallback(
    async (noteId: string) => {
      try {
        const fetchedNote = await currentPersonalNotesAPI.getPersonalNote(
          noteId,
        );

        if (fetchedNote) {
          fetchedNote.isTargetNote = true;
        }

        return fetchedNote;
      } catch (error) {
        return null;
      }
    },
    [currentPersonalNotesAPI],
  );

  const debouncedSetNextPage = useMemo(
    () =>
      debounce(() => {
        setFilters((prev) => ({
          ...prev,
          pageNumber: prev.pageNumber ? prev.pageNumber + 1 : 1,
        }));
      }, 300),
    [setFilters],
  );

  const onNextPage = useCallback(() => {
    if (hasMorePages && !loadingRef.current) {
      debouncedSetNextPage();
    }
  }, [hasMorePages, debouncedSetNextPage]);

  // Sync URL with filters and selectedNote (without page reload)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Build filter object from current filters state
    const filterObj: {
      search?: string;
      filterType?: FilterType;
      pageNumber?: number;
    } = {};

    if (searchFilter) {
      filterObj.search = searchFilter; // Already encoded for API
    }
    if (typeFilter) {
      filterObj.filterType = typeFilter;
    }

    // Include pageNumber in filter object (only if > 0)
    const currentPageParam = filters.pageNumber ?? 0;
    if (currentPageParam > 0) {
      filterObj.pageNumber = currentPageParam;
    }

    // Encode filters into single filter parameter
    const hasFilters =
      filterObj.search || filterObj.filterType || filterObj.pageNumber;
    if (hasFilters) {
      params.set('filter', encodeQuery(filterObj));
    } else {
      params.delete('filter');
    }

    // Update selected note (always separate parameter)
    const selectedId = selectedNote?.id;
    if (selectedId) {
      params.set('note', String(selectedId));
    } else {
      params.delete('note');
    }

    // Preserve targetNote if it exists
    const targetNoteId = targetNoteRef.current;
    if (targetNoteId) {
      params.set('targetNote', String(targetNoteId));
    } else {
      params.delete('targetNote');
    }

    // Update URL without page reload
    const newSearch = params.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    window.history.replaceState(window.history.state, '', newUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchFilter,
    typeFilter,
    filters.pageNumber,
    selectedNote,
    location.pathname,
  ]);

  // Helper function to load pages (single or multiple)
  const loadPages = useCallback(
    async (pageNumber: number): Promise<PersonalNote[]> => {
      if (pageNumber > 0) {
        // Load all pages 0..pageNumber in parallel
        const pages = Array.from({ length: pageNumber + 1 }, (_, i) => i);
        const results = await Promise.all(
          pages.map((p) =>
            currentPersonalNotesAPI.filterPersonalNotes({
              search: searchFilter,
              filterType: typeFilter,
              pageNumber: p,
            }),
          ),
        );
        return ([] as PersonalNote[]).concat(...results);
      } else {
        // Load first page
        return await currentPersonalNotesAPI.filterPersonalNotes({
          search: searchFilter,
          filterType: typeFilter,
          pageNumber: 0,
        });
      }
    },
    [searchFilter, typeFilter, currentPersonalNotesAPI],
  );

  // Helper function to select note from loaded notes based on priority
  const selectNoteFromLoaded = useCallback(
    (
      loadedNotes: PersonalNote[],
      options: {
        noteId?: string | null;
        targetNoteId?: string | null;
        createdNoteId?: string | null;
        preferFirst?: boolean;
      } = {},
    ) => {
      const { noteId, targetNoteId, createdNoteId, preferFirst } = options;

      // Priority 1: noteId from query
      if (noteId) {
        const found = loadedNotes.find((n) => n.id === noteId);
        if (found) return found;
      }

      // Priority 2: createdNoteId
      if (createdNoteId) {
        const found = loadedNotes.find((n) => n.id === createdNoteId);
        if (found) return found;
      }

      // Priority 3: targetNoteId
      if (targetNoteId) {
        const found = loadedNotes.find((n) => n.id === targetNoteId);
        if (found) return found;
      }

      // Priority 4: first note (if preferFirst and not mobile)
      if (preferFirst && loadedNotes.length > 0 && !isMobile()) {
        return loadedNotes[0];
      }

      return null;
    },
    [],
  );

  // Fetch notes when filters change
  useEffect(() => {
    const loadNotes = async () => {
      const isInitialLoad = isInitialLoadRef.current;
      const currentPage = filters.pageNumber ?? 0;
      const targetNoteId = targetNoteRef.current;

      setLoadingState(true);
      if (!currentPage) {
        setNotes([]);
        setSelectedNote(null);
      }

      try {
        let loadedNotes: PersonalNote[] = [];

        // Handle targetNote on initial load
        if (isInitialLoad && targetNoteId) {
          const targetNote = await fetchTargetNote(targetNoteId);
          if (targetNote) {
            // Load regular notes
            loadedNotes = await loadPages(currentPage);

            // Remove target note if exists in regular notes and prepend
            const filteredNotes = loadedNotes.filter(
              (note) => note.id !== targetNoteId,
            );
            loadedNotes = [targetNote, ...filteredNotes];
          } else {
            // Target note not found, load normally
            loadedNotes = await loadPages(currentPage);
          }
        } else {
          // Regular load
          if (isInitialLoad && currentPage > 0) {
            // Initial load with page > 0 - load all pages 0..currentPage
            loadedNotes = await loadPages(currentPage);
          } else if (!isInitialLoad && currentPage > 0) {
            // Not initial load and page > 0 - accumulate (add to existing)
            const pageNotes = await currentPersonalNotesAPI.filterPersonalNotes(
              {
                search: searchFilter,
                filterType: typeFilter,
                pageNumber: currentPage,
              },
            );
            setNotes((prev) => [...prev, ...pageNotes]);
            setHasMorePages(pageNotes.length > 0);
            setLoadingState(false);
            return;
          } else {
            // Load first page (replace)
            loadedNotes = await loadPages(0);
          }
        }

        // Update notes
        setNotes(loadedNotes);
        setHasMorePages(loadedNotes.length > 0);

        // Select note logic
        if (isInitialLoad) {
          // Initial load - use query params logic
          const selected = selectNoteFromLoaded(loadedNotes, {
            noteId: noteRef.current,
            targetNoteId,
            preferFirst: true,
          });
          setSelectedNote(selected);
          isInitialLoadRef.current = false;
        } else if (currentPage === 0) {
          // Filter change (not initial load, page 0) - select note and reset targetNote
          const createdNoteId = createdNoteRef.current?.id ?? null;
          const selected = selectNoteFromLoaded(loadedNotes, {
            createdNoteId,
            preferFirst: true,
          });
          setSelectedNote(selected);
          if (createdNoteRef.current) {
            createdNoteRef.current = null;
          }
          // Reset targetNote
          targetNoteRef.current = null;
        }
      } catch (error) {
        setNotes([]);
        setSelectedNote(null);
        setHasMorePages(false);
        enqueueSnackbar(
          'Something went wrong while fetching notes, please try again',
          {
            variant: 'error',
          },
        );
      } finally {
        setLoadingState(false);
      }
    };

    loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter, typeFilter, filters.pageNumber]);

  const updateNote = useCallback(
    async (note: PersonalNote) => {
      try {
        setSaving(true);
        const updatedNote = await currentPersonalNotesAPI.updatePersonalNotes(
          note,
        );

        setNotes((prevNotes) => {
          const copyPrevNotes = [...prevNotes];
          const noteIndex = copyPrevNotes.findIndex((n) => n.id === note.id);
          if (noteIndex !== -1) {
            copyPrevNotes[noteIndex] = updatedNote;
          }
          return copyPrevNotes;
        });

        return updatedNote;
      } catch (error) {
        enqueueSnackbar(
          'Something went wrong while updating note, please try again',
          {
            variant: 'error',
          },
        );
      } finally {
        setSaving(false);
      }
    },
    [currentPersonalNotesAPI, enqueueSnackbar],
  );

  const createNote = useCallback(
    async (note: PersonalNote) => {
      try {
        setSaving(true);
        const newNote = await currentPersonalNotesAPI.createPersonalNotes(note);

        if (!newNote) {
          throw new Error('Error creating note');
        }

        return newNote;
      } catch (error) {
        enqueueSnackbar(
          'Something went wrong while creating note, please try again',
          {
            variant: 'error',
          },
        );
      } finally {
        setSaving(false);
      }
    },
    [currentPersonalNotesAPI, enqueueSnackbar],
  );

  const deleteNote = useCallback(
    async (note: PersonalNote) => {
      try {
        setDeleting(true);
        const response = await currentPersonalNotesAPI.removePersonalNote(note);

        const wasSelectedNote = selectedNote?.id === note.id;

        let updatedNotes: PersonalNote[] = [];
        setNotes((prevNotes) => {
          updatedNotes = prevNotes.filter((n) => n.id !== note.id);
          return updatedNotes;
        });

        // Update selectedNote if deleted note was selected
        if (wasSelectedNote) {
          if (updatedNotes.length > 0 && !isMobile()) {
            setSelectedNote(updatedNotes[0]);
          } else {
            setSelectedNote(null);
          }
        }

        return response;
      } catch (error) {
        enqueueSnackbar(
          'Something went wrong while deleting note, please try again',
          {
            variant: 'error',
          },
        );
      } finally {
        setDeleting(false);
      }
    },
    [currentPersonalNotesAPI, enqueueSnackbar, selectedNote, setSelectedNote],
  );

  const closeAllBlocks = useCallback(() => {
    if (activeBlock) {
      setActiveBlock(null);
    }
  }, [activeBlock]);

  const toggleAttachments = useCallback(() => {
    setActiveBlock((prev) =>
      prev === NotesBlockType.Attachment ? null : NotesBlockType.Attachment,
    );
  }, []);

  const toggleComments = useCallback(() => {
    setActiveBlock((prev) =>
      prev === NotesBlockType.Comment ? null : NotesBlockType.Comment,
    );
  }, []);

  const deleteComment = useCallback(
    async (commentId: string) => {
      try {
        await commentsAPI.deleteComment(commentId);
        setComments((prevComments) => {
          return prevComments.filter((c) => c.id !== commentId);
        });
      } catch (error) {
        enqueueSnackbar(
          'Something went wrong while deleting comment, please try again',
          {
            variant: 'error',
          },
        );
      }
    },
    [enqueueSnackbar],
  );

  const handleSaveNote = useCallback(async () => {
    if (selectedNote) {
      const updatedNoteData: PersonalNote = {
        ...selectedNote,
        content: value,
        attachmentRefs: JSON.stringify(attachments),
      };
      const updatedNote = await updateNote(updatedNoteData);

      if (updatedNote) {
        setSelectedNote(isMobile() ? null : updatedNote);

        enqueueSnackbar('Note updated successfully', {
          variant: 'success',
        });
      }
    }
  }, [
    attachments,
    enqueueSnackbar,
    selectedNote,
    setSelectedNote,
    updateNote,
    value,
  ]);

  const handleNoteRemove = useCallback(
    async (callback: Function) => {
      if (!selectedNote) {
        setSelectedNote(notes[0]);
        return;
      }

      const res = await deleteNote(selectedNote);

      if (res) {
        enqueueSnackbar('Note removed successfully', {
          variant: 'success',
        });
      }

      callback();
    },
    [deleteNote, enqueueSnackbar, notes, selectedNote, setSelectedNote],
  );

  const handleCardSelect = useCallback(
    (note: PersonalNote) => {
      const noteId = String(note.id);
      const selectedId = String(selectedNote?.id);
      if (noteId === selectedId) {
        return;
      }

      if (isDirty) {
        setBeforeUnloadState({ action: 'select', note });
      } else {
        setBeforeUnloadState(null);
        setSelectedNote(note);
      }
    },
    [selectedNote, isDirty, setBeforeUnloadState, setSelectedNote],
  );

  const handleCardClose = useCallback(() => {
    if (isDirty) {
      setBeforeUnloadState({ action: 'close' });
    } else {
      setBeforeUnloadState(null);
      setSelectedNote(null);
    }
  }, [isDirty, setBeforeUnloadState, setSelectedNote]);

  const addNewNote = useCallback(async () => {
    const newNoteData: PersonalNote = hasRole(Role.Founder)
      ? {
          content: '',
          attachmentRefs: '[]',
          type: 'GeneralNote',
          tenantId: user.id,
          founderId: tokenData?.identityid || null,
          date: new Date().toISOString(),
        }
      : {
          content: '',
          attachmentRefs: '[]',
          type: 'GeneralNote',
          tenantId: user.id,
          mentorId: tokenData?.identityid || null,
          date: new Date().toISOString(),
        };
    const newNote = await createNote(newNoteData);
    const hasFilters = !!searchFilter || !!typeFilter;

    if (newNote) {
      if (hasFilters) {
        resetFilters();
        createdNoteRef.current = newNote;
      } else {
        setNotes((prevNotes) =>
          newNote ? [newNote, ...prevNotes] : prevNotes,
        );
        // Store the new note to select it after it's added to the list
        pendingNewNoteRef.current = newNote;
      }
    }

    // scroll card list to the top
    try {
      const cardList = document.getElementById('cardList');
      cardList?.scrollTo(0, 0);
    } catch (e) {
      console.error(e);
    }
  }, [
    createNote,
    hasRole,
    resetFilters,
    tokenData?.identityid,
    user.id,
    searchFilter,
    typeFilter,
  ]);

  const handleAddNewNote = useCallback(() => {
    if (isDirty) {
      setBeforeUnloadState({ action: 'add' });
    } else {
      setBeforeUnloadState(null);
      addNewNote();
    }
  }, [addNewNote, isDirty]);

  useEffect(() => {
    setActiveBlock(null);
  }, [selectedNote?.id]);

  // Select newly created note after it's added to the list
  useEffect(() => {
    if (pendingNewNoteRef.current && notes.length > 0) {
      const noteToSelect = notes.find(
        (note) => note.id === pendingNewNoteRef.current?.id,
      );
      if (noteToSelect) {
        setSelectedNote(noteToSelect);
        pendingNewNoteRef.current = null;
      }
    }
  }, [notes, setSelectedNote]);

  // Auto-select note if selectedNote is not found in notes
  useEffect(() => {
    // Skip during initial load
    if (isInitialLoadRef.current) return;

    // Skip if no notes
    if (notes.length === 0) {
      if (selectedNote) {
        setSelectedNote(null);
      }
      return;
    }

    // Check if selectedNote exists in notes
    const findSelectedNote = selectedNote
      ? notes.some((note) => {
          const noteId = note.id;
          const selectedId = selectedNote.id;
          if (!noteId || !selectedId) return false;
          return noteId === selectedId;
        })
      : false;

    // If selectedNote is found, do nothing
    if (findSelectedNote) {
      return;
    }

    // Avoid auto-selecting while a target note is pending
    if (targetNoteRef.current) return;

    // Auto-select first note
    if (notes.length > 0 && !isMobile()) {
      setSelectedNote(notes[0]);
    } else {
      setSelectedNote(null);
    }
  }, [notes, selectedNote, setSelectedNote]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSetNextPage.cancel();
    };
  }, [debouncedSetNextPage]);

  const contextValue: PersonalNotesContextProps = {
    notes,
    selectedNote,
    isReadOnlySelectedNote,
    attachments,
    activeBlock,
    showAttachments: activeBlock === NotesBlockType.Attachment,
    showComments: activeBlock === NotesBlockType.Comment,
    comments,
    loadingComments,
    loading,
    saving,
    deleting,
    searchFilter,
    typeFilter,
    searchValue,
    value,
    isDirty,
    beforeUnloadState,
    typeFilterOptions,
    handleSearch,
    setBeforeUnloadState,
    setValue,
    setSearchValue,
    setSelectedNote,
    setAttachments,
    toggleAttachments,
    toggleComments,
    setComments,
    setLoadingComments,
    setSaving,
    closeAllBlocks,
    createNote,
    updateNote,
    deleteNote,
    onNextPage,
    setSearchFilter,
    deleteComment,
    resetFilters,
    handleSaveNote,
    handleNoteRemove,
    addNewNote,
    handleAddNewNote,
    handleCardSelect,
    handleCardClose,
    setTypeFilter,
  };

  return (
    <PersonalNotesContext.Provider value={contextValue}>
      {children}
    </PersonalNotesContext.Provider>
  );
};

export const usePersonalNotes = () => {
  const context = useContext(PersonalNotesContext);
  if (!context) {
    throw new Error(
      'usePersonalNotes must be used within a PersonalNotesProvider',
    );
  }
  return context;
};
